// TawkWrapper.tsx
import { useEffect } from "react";
import TawkMessengerReact from "@tawk.to/tawk-messenger-react";
import { useStore } from "@/stores/store.js";
import { useAccessControl } from "@/hooks/useAccessControl";
import { trackFeatureUsage } from "@/utils/gtm";

const ANALYST_PROPERTY_ID = "58707db72438f53b0a2585c1";
const SALES_PROPERTY_ID = "59cccc46c28eca75e4622dc0";

const capitalize = (s = "") =>
  s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

const TawkWrapper = () => {
  const { tokenParsed } = useStore();
  const { hasLiveAccess } = useAccessControl();

  const propertyId = hasLiveAccess ? ANALYST_PROPERTY_ID : SALES_PROPERTY_ID;

  useEffect(() => {
    if (!tokenParsed) return;

    const { given_name, family_name, email } = tokenParsed;
    const first_name = capitalize(given_name);
    const last_name = capitalize(family_name);

    // Bail until the Tawk API is available
    if (!window.Tawk_API) return;

    window.Tawk_API.onLoad = () => {
      const attrs = { name: `${first_name} ${last_name}`, email };

      const trySetAttributes = (attempt) => {
        window.Tawk_API.setAttributes(attrs, (err) => {
          if (err) {
            if (attempt < 2) {
              console.warn(`[Tawk] setAttributes failed (attempt ${attempt}), retrying in 3s...`);
              setTimeout(() => trySetAttributes(attempt + 1), 3000);
            } else {
              console.error("[Tawk] setAttributes failed after retry:", err);
            }
          }
        });
      };

      trySetAttributes(1);
    };
  }, [tokenParsed, propertyId]);

  /**
   * Chat instrumentation.
   *
   * Tawk was never tracked, so "did they ever reach the desk" — one of the
   * better predictors we have of a trial converting — was invisible. Two
   * signals, because they mean different things: `opened` is the intent, and
   * `message_sent` is the conversation actually starting. Splitting them makes
   * the gap between the two readable, which is where "the desk was offline"
   * shows up.
   *
   * Only reported for the ANALYST property. The sales property is a different
   * conversation with a different audience, and folding both into one feature
   * name would make the series unreadable.
   *
   * `analyst_chat`/`opened` is also what completes the onboarding rail's chat
   * step — the rail observes this like every other step, rather than the chat
   * knowing the rail exists.
   */
  useEffect(() => {
    if (!window.Tawk_API || propertyId !== ANALYST_PROPERTY_ID) return;

    // Tawk fires onChatMaximized on every expand, and a visitor may send
    // several messages; both events are deduped to once per page so the series
    // counts people rather than clicks.
    let reportedOpen = false;
    let reportedMessage = false;

    window.Tawk_API.onChatMaximized = () => {
      if (reportedOpen) return;
      reportedOpen = true;
      trackFeatureUsage({
        feature_name: "analyst_chat",
        feature_action: "opened",
      });
    };

    window.Tawk_API.onChatMessageVisitor = () => {
      if (reportedMessage) return;
      reportedMessage = true;
      trackFeatureUsage({
        feature_name: "analyst_chat",
        feature_action: "message_sent",
      });
    };

    return () => {
      // Leave the handlers in a known state rather than pointing at a closure
      // over a stale `reported*` flag after a property switch.
      if (!window.Tawk_API) return;
      window.Tawk_API.onChatMaximized = undefined;
      window.Tawk_API.onChatMessageVisitor = undefined;
    };
  }, [propertyId]);

  return (
    <TawkMessengerReact
      key={propertyId}
      propertyId={propertyId}
      widgetId="default"
    />
  );
};

export default TawkWrapper;
